import { csrfToken } from '../services/browserSession';
import { track } from '../services/measurement';

export const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const isSafariBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Safari/i.test(ua) && !/(Chrome|CriOS|FxiOS|EdgiOS|OPiOS|Android)/i.test(ua);
};

export const getApiBaseUrl = () => {
  const configuredUrl = process.env.REACT_APP_API_URL?.replace(/\/+$/, '');
  if (configuredUrl) return configuredUrl;
  return window.location.origin;
};

export const INSTAGRAM_OAUTH_STARTED_EVENT = 'foth:instagram-oauth-started';
export const INSTAGRAM_OAUTH_COMPLETED_MESSAGE = 'foth:instagram-oauth-complete';
export const INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY = 'foth_instagram_oauth_completion';
export const INSTAGRAM_OAUTH_CHANNEL = 'foth-instagram-oauth';

const INSTAGRAM_OAUTH_PENDING_KEY = 'foth_instagram_oauth_pending';
const INSTAGRAM_OAUTH_START_LOCK_KEY = 'foth_instagram_oauth_start_lock';
const INSTAGRAM_OAUTH_START_LOCK_MS = 3000;
export const INSTAGRAM_OAUTH_POPUP_NAME = 'foth-instagram-oauth';
const INSTAGRAM_POPUP_FEATURES = 'popup=yes,width=560,height=780,resizable=yes,scrollbars=yes';

let instagramPopup = null;

const createInstagramAttemptId = () => {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  if (typeof window.crypto?.getRandomValues === 'function') {
    const values = new Uint32Array(4);
    window.crypto.getRandomValues(values);
    return Array.from(values, value => value.toString(36)).join('-');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const normalizeInstagramReturnPath = (returnTo = '/complete-profile') => {
  if (typeof returnTo !== 'string') return '/complete-profile';
  const candidate = returnTo.trim();
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.includes('\\')) {
    return '/complete-profile';
  }
  return candidate;
};

export const buildInstagramConnectRequest = (
  returnTo = '/complete-profile',
  { popup = false, attemptId = '' } = {}
) => {
  const safeReturnTo = normalizeInstagramReturnPath(returnTo);
  sessionStorage.setItem('foth_instagram_return_to', safeReturnTo);
  const callbackQuery = new URLSearchParams({ next: safeReturnTo });
  if (popup) callbackQuery.set('popup', '1');
  if (attemptId) callbackQuery.set('attempt_id', attemptId);
  const callbackTarget = `/instagram/connected?${callbackQuery.toString()}`;
  sessionStorage.setItem('foth_instagram_callback_target', callbackTarget);
  const fields = {
    csrfmiddlewaretoken: csrfToken(),
    next: callbackTarget,
  };

  if (isIOSDevice()) {
    fields.platform = 'ios';
  }

  return {
    url: `${getApiBaseUrl()}/api/instagram/connect/`,
    fields,
  };
};

export const getPendingInstagramConnection = () => {
  const rawPending = sessionStorage.getItem(INSTAGRAM_OAUTH_PENDING_KEY);
  if (!rawPending) return null;

  try {
    const pending = JSON.parse(rawPending);
    const isFresh = Date.now() - Number(pending.openedAt || 0) < 15 * 60 * 1000;
    if (!isFresh) {
      sessionStorage.removeItem(INSTAGRAM_OAUTH_PENDING_KEY);
      return null;
    }
    return pending;
  } catch (error) {
    sessionStorage.removeItem(INSTAGRAM_OAUTH_PENDING_KEY);
    return null;
  }
};

export const clearPendingInstagramConnection = () => {
  sessionStorage.removeItem(INSTAGRAM_OAUTH_PENDING_KEY);
};

const rememberPendingInstagramConnection = (returnTo, attemptId) => {
  const previous = getPendingInstagramConnection();
  const sourcePath = `${window.location.pathname}${window.location.search || ''}`;
  const pending = {
    returnTo,
    sourcePath,
    attemptId,
    openedAt: Date.now(),
    attempt: Number(previous?.attempt || 0) + 1,
  };
  sessionStorage.setItem(INSTAGRAM_OAUTH_PENDING_KEY, JSON.stringify(pending));
  window.dispatchEvent(new CustomEvent(INSTAGRAM_OAUTH_STARTED_EVENT, { detail: pending }));
  return pending;
};

const CONFIRMATION_KEY = 'foth_instagram_connection_confirmation';
const TOAST_CONFIRMATION_KEY = 'foth_instagram_connection_toast';

export const rememberInstagramConnectionConfirmation = ({ returnTo = '/', userType = '', username = '' } = {}) => {
  const confirmation = JSON.stringify({
    returnTo,
    userType,
    username,
    createdAt: Date.now(),
  });
  sessionStorage.setItem(CONFIRMATION_KEY, confirmation);
  sessionStorage.setItem(TOAST_CONFIRMATION_KEY, confirmation);
};

const consumeStoredConfirmation = (storageKey, expectedPath = '') => {
  const rawConfirmation = sessionStorage.getItem(storageKey);
  if (!rawConfirmation) return null;

  try {
    const confirmation = JSON.parse(rawConfirmation);
    const isFresh = Date.now() - Number(confirmation.createdAt || 0) < 5 * 60 * 1000;
    const matchesPath = !expectedPath || !confirmation.returnTo || confirmation.returnTo === expectedPath;

    if (!isFresh) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    if (!matchesPath) return null;

    sessionStorage.removeItem(storageKey);
    return confirmation;
  } catch (error) {
    sessionStorage.removeItem(storageKey);
    return null;
  }
};

export const consumeInstagramConnectionConfirmation = (expectedPath = '') => (
  consumeStoredConfirmation(CONFIRMATION_KEY, expectedPath)
);

export const consumeInstagramConnectionToast = (expectedPath = '') => (
  consumeStoredConfirmation(TOAST_CONFIRMATION_KEY, expectedPath)
);

export const beginInstagramConnect = (
  returnTo = '/complete-profile',
  { attemptId: requestedAttemptId = '', usePopup = true } = {}
) => {
  const safeReturnTo = normalizeInstagramReturnPath(returnTo);
  const startedAt = Date.now();
  const activePending = getPendingInstagramConnection();

  if (activePending && instagramPopup) {
    try {
      if (!instagramPopup.closed) {
        instagramPopup.focus();
        return activePending;
      }
    } catch {
      // If the popup state cannot be inspected, the lock below still prevents rapid duplicates.
    }
  }

  const activeLock = Number(localStorage.getItem(INSTAGRAM_OAUTH_START_LOCK_KEY) || 0);
  if (startedAt - activeLock < INSTAGRAM_OAUTH_START_LOCK_MS) {
    try {
      instagramPopup?.focus();
    } catch {
      // The active attempt remains valid even if focus is restricted.
    }
    if (activePending) return activePending;
  }
  localStorage.setItem(INSTAGRAM_OAUTH_START_LOCK_KEY, String(startedAt));

  // Safari frequently leaves a window opened with window.open('', ...) blank
  // when the subsequent form navigation crosses to Instagram. Keep OAuth in
  // the current tab there (and on iOS), where the callback route can render a
  // recovery state instead of strand the user on an empty tab.
  const openPopup = usePopup && !isIOSDevice() && !isSafariBrowser();
  instagramPopup = openPopup
    ? window.open('', INSTAGRAM_OAUTH_POPUP_NAME, INSTAGRAM_POPUP_FEATURES)
    : null;
  if (instagramPopup) {
    try {
      instagramPopup.opener = null;
      instagramPopup.focus();
    } catch {
      // The browser may restrict popup properties; form targeting still works.
    }
  }

  const attemptId = requestedAttemptId || createInstagramAttemptId();
  const { url, fields } = buildInstagramConnectRequest(safeReturnTo, {
    popup: Boolean(instagramPopup),
    attemptId,
  });
  const pending = rememberPendingInstagramConnection(safeReturnTo, attemptId);
  track('instagram_connect_started', {
    platform: isIOSDevice() ? 'ios' : isSafariBrowser() ? 'safari' : 'web',
  });

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.style.display = 'none';
  if (instagramPopup) form.target = INSTAGRAM_OAUTH_POPUP_NAME;

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  window.setTimeout(() => form.remove(), 0);
  return pending;
};

export const publishInstagramConnectionCompletion = (payload = {}) => {
  const completion = {
    type: INSTAGRAM_OAUTH_COMPLETED_MESSAGE,
    returnTo: normalizeInstagramReturnPath(payload.returnTo || '/complete-profile'),
    attemptId: typeof payload.attemptId === 'string' ? payload.attemptId.slice(0, 128) : '',
    createdAt: Date.now(),
  };

  localStorage.setItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY, JSON.stringify(completion));
  track('instagram_connect_completed', { platform: 'web' });
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(INSTAGRAM_OAUTH_CHANNEL);
    channel.postMessage(completion);
    channel.close();
  }
  if (window.opener && !window.opener.closed) {
    window.opener.postMessage(completion, window.location.origin);
  }
  return completion;
};
